import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import { CallService } from '../../services/callService';
import { CadInboxItem } from '../../types/Call';
import { SectionLabel } from './CadRecordParts';
import { formatDateTime, visuallyHidden } from './cadRecordDisplay';

/** The first few reports; the rest are one click away. A shelf of hundreds must not push the drop zone off the screen. */
const VISIBLE_COUNT = 5;

const HOLD_WORDING: Record<NonNullable<CadInboxItem['holdReason']>, string> = {
  ManyCalls: 'Several calls share this incident number. Choose the right one.',
  CallHasRecord: 'The call this matched already has a CAD report.',
  NoEventId: 'No event ID could be read from this report.',
  OutOfScope: 'The matching call is outside the team of the person who uploaded this report.',
};

export const WAITING_HEADING_ID = 'cad-waiting-heading';

interface CadWaitingListProps {
  callId: string;
  /** This call's incident number, named in the confirmation when a report does not match it. */
  incidentNumber: string;
  /** Attaches the report to the call. Resolves true when it was attached. */
  onAttach: (inboxItemId: string) => Promise<boolean>;
  disabled: boolean;
}

/**
 * CAD reports uploaded in bulk that no call has claimed yet, offered on a call that has no report.
 * The ones that name this call come first and say so. Attaching is one click; a report that does not
 * name this call asks first, because the only wrong outcome here is one call's record on another.
 * Renders nothing when there is nothing waiting, so the drop zone below is the whole tab.
 */
export const CadWaitingList: React.FC<CadWaitingListProps> = ({ callId, incidentNumber, onAttach, disabled }) => {
  const [items, setItems] = useState<CadInboxItem[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // The dialogs keep the item they were opened for after they close, so their text does not blank
  // during the close transition (a screen reader would read "undefined").
  const [attachTarget, setAttachTarget] = useState<CadInboxItem | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CadInboxItem | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // One always-mounted polite region: text set inside a region that mounts with it is not read.
  const [announcement, setAnnouncement] = useState('');
  const listRef = useRef<HTMLUListElement>(null);

  const load = useCallback(() => {
    setLoadFailed(false);
    CallService.listCadInbox(callId)
      .then(setItems)
      .catch(() => {
        setItems(null);
        setLoadFailed(true);
      });
  }, [callId]);

  useEffect(() => {
    let cancelled = false;
    CallService.listCadInbox(callId)
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [callId]);

  const attach = useCallback(
    async (item: CadInboxItem) => {
      setAttachOpen(false);
      setBusyId(item.inboxItemId);
      setAnnouncement(`Attaching ${item.originalFileName}.`);
      const attached = await onAttach(item.inboxItemId);
      setBusyId(null);
      // On success the tab swaps to the attached record, takes focus there, and this list unmounts.
      // On failure (someone else attached it first, or the call took a report meanwhile) the list is
      // stale: read it again and say so.
      if (!attached) {
        setAnnouncement('That report could not be attached. The list has been refreshed.');
        load();
      }
    },
    [load, onAttach]
  );

  const remove = useCallback(
    async (item: CadInboxItem, index: number) => {
      setDeleteOpen(false);
      setBusyId(item.inboxItemId);
      try {
        await CallService.deleteCadInboxItem(item.inboxItemId);
        const remaining = (items ?? []).filter((i) => i.inboxItemId !== item.inboxItemId);
        setItems(remaining);
        setAnnouncement(
          `Deleted ${item.originalFileName}. ${remaining.length === 0 ? 'No reports are waiting.' : `${remaining.length} waiting.`}`
        );
        // The Delete button that opened the dialog is gone: put focus on the row that took its place,
        // the one before it if it was the last, or the heading when the list is empty.
        requestAnimationFrame(() => {
          const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('button[data-delete]');
          const target = buttons?.[Math.min(index, (buttons?.length ?? 1) - 1)];
          (target ?? document.getElementById(WAITING_HEADING_ID))?.focus();
        });
      } catch {
        // Surfaced by the service layer's notification.
      } finally {
        setBusyId(null);
      }
    },
    [items]
  );

  const liveRegion = (
    <Box role="status" aria-live="polite" aria-atomic="true" sx={visuallyHidden}>
      {announcement}
    </Box>
  );

  if (loadFailed) {
    return (
      <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>
        The waiting CAD reports could not be loaded. Reports may be waiting that are not shown here.
      </Alert>
    );
  }

  if (!items) return null;

  // Emptied by a delete: keep the heading so focus has somewhere to go and the result is announced.
  if (items.length === 0 && !announcement) return null;

  const shown = showAll ? items : items.slice(0, VISIBLE_COUNT);

  return (
    <Stack spacing={1.5} component="section" aria-labelledby={WAITING_HEADING_ID}>
      <SectionLabel id={WAITING_HEADING_ID} focusable>
        CAD reports waiting for a call ({items.length})
      </SectionLabel>
      {liveRegion}
      <Stack ref={listRef} spacing={1} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {shown.map((item) => {
          const busy = busyId === item.inboxItemId;
          const nameId = `cad-waiting-name-${item.inboxItemId}`;
          return (
            <Paper
              key={item.inboxItemId}
              component="li"
              variant="outlined"
              sx={{ px: 2, py: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                <PictureAsPdfOutlined sx={{ color: 'text.secondary' }} aria-hidden />
                <Box minWidth={0}>
                  <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                    <Typography id={nameId} variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                      {item.originalFileName}
                    </Typography>
                    {item.matchesCall && (
                      <Chip label="Matches this call" size="small" color="success" variant="outlined" />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
                    {item.eventId ? `Event ${item.eventId}. ` : ''}
                    Uploaded by {item.uploadedBy} on {formatDateTime(item.uploadedAt)}
                  </Typography>
                  {item.holdReason && (
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
                      {HOLD_WORDING[item.holdReason]}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Button
                  variant={item.matchesCall ? 'contained' : 'outlined'}
                  size="small"
                  disabled={disabled || busyId !== null}
                  aria-describedby={nameId}
                  onClick={() => {
                    if (item.matchesCall) {
                      void attach(item);
                    } else {
                      setAttachTarget(item);
                      setAttachOpen(true);
                    }
                  }}
                >
                  {busy ? 'Attaching…' : 'Attach'}
                </Button>
                <IconButton
                  size="small"
                  data-delete
                  disabled={disabled || busyId !== null}
                  onClick={() => {
                    setDeleteTarget(item);
                    setDeleteOpen(true);
                  }}
                  aria-label={`Delete ${item.originalFileName} from the waiting reports`}
                >
                  <DeleteOutlined fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          );
        })}
      </Stack>
      {items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No CAD reports are waiting.
        </Typography>
      )}
      {items.length > VISIBLE_COUNT && (
        <Box>
          <Button size="small" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show fewer' : `Show all ${items.length}`}
          </Button>
        </Box>
      )}

      <Dialog
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="cad-waiting-attach-title"
        aria-describedby="cad-waiting-attach-text"
      >
        <DialogTitle id="cad-waiting-attach-title">Attach a report that does not match?</DialogTitle>
        <DialogContent>
          <DialogContentText id="cad-waiting-attach-text">
            {attachTarget?.eventId
              ? `This report's event ID is ${attachTarget.eventId}.`
              : `This report's file name is ${attachTarget?.originalFileName}.`}{' '}
            This call's incident number is {incidentNumber || 'not set'}. Attach it anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* Cancel first and focused: Enter on opening must not confirm the unsafe choice. */}
          <Button autoFocus onClick={() => setAttachOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => attachTarget && void attach(attachTarget)}>
            Attach anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="cad-waiting-delete-title"
        aria-describedby="cad-waiting-delete-text"
      >
        <DialogTitle id="cad-waiting-delete-title">Delete this waiting report?</DialogTitle>
        <DialogContent>
          <DialogContentText id="cad-waiting-delete-text">
            {deleteTarget?.originalFileName} will be removed for good. It is not attached to any call.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() =>
              deleteTarget &&
              void remove(
                deleteTarget,
                items.findIndex((i) => i.inboxItemId === deleteTarget.inboxItemId)
              )
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
